import Object3D from "_src/engine/object";
import Chunk from "_src/engine/chunk";
import { ChunkInDrawable } from "_src/engine/chunk";
import {cullingFrontFace, drawQueryState, memoryType} from "_src/engine/constants";
import GL from "_src/engine/gl";
import extensions from "_src/engine/extensions";
import minmaxUtils from "_src/engine/utility/minmax";
import output from "_src/engine/output.js";

import {vec3, vec4, quat, mat4} from "_src/libs/gl-matrix";
import memoryProfiler from "_src/engine/memory_profiler";
import DefaultShader from "_src/shaders/default/default_shader";

const _VEC3_TMP = vec3.create();
const _VEC4_TMP = vec4.create();
const _VEC4_TMP2 = vec4.create();

const _MAT4_TMP = mat4.create();

export default class Drawable extends Object3D {

  constructor(shader) {

    super();

    this.isDrawable = true;
    this.shader = shader;

    this.chunks = [];
    this.importedModelsForInstancing = [];

    this.properties = {
      diffuseColor: new Float32Array([0.8, 0.8, 0.8, 1.0]),
      enableColorCorrection: false,
      enableLinearColorSpace: false,
      enableLambertDiffuse: false,
      reflectivity: new Float32Array([0.5, 0.5, 0.0]),
    };

    this.calculateMinmax = false;
    this.minmaxLocal = new Array(6);
    this.minmaxWorld = new Array(6);

    minmaxUtils.reset(this.minmaxLocal);
    minmaxUtils.reset(this.minmaxWorld);

    this.textures = [];

    this._chunkProperties = new WeakMap();
    this.isHidden = false;

  }

  addGeometryChunk(geometry) {

    const chunk = new Chunk();

    this.chunks.push(chunk);
    this._chunkProperties.set(chunk, new ChunkInDrawable());
    this.setChunkGeometry(chunk, geometry);
    this.updateChunkBBox(chunk);

  }

  addChunk(chunk) {

    this.chunks.push(chunk);
    this._chunkProperties.set(chunk, new ChunkInDrawable());

    if (!chunk.isEmpty()) {
      this.updateChunkBBox(chunk);
    }

    const sceneOwner = this.getRootNode();
    if (sceneOwner) {
      chunk.requestMemoryUsageReport(sceneOwner);
    }

  }

  setChunkGeometry(chunk, geometryBuffer) {
    const sceneOwner = this.getRootNode();

    if (chunk.geometry && chunk.geometry != geometryBuffer) {
      chunk.reportMemoryRelease();
      chunk.geometry.releaseMemory();
      chunk.geometry.terminateDataBuilding();
    }

    chunk.geometry = geometryBuffer;

    const layout = chunk.geometry.getLayout();
    for (let attrName in layout) {
      chunk.markAttributeDirty(attrName);
    }

    this._chunkProperties.get(chunk).activeShaderProgram = null;

    if (sceneOwner) {
      chunk.requestMemoryUsageReport(sceneOwner);
    }

  }

  importModelsForInstancing(modelsArray) {
    this.reportImportedAttributesRelease();
    this.importedModelsForInstancing = modelsArray;

    for (const chunk of this.chunks) {
      this._chunkProperties.get(chunk).activeShaderProgram = null;
    }
  }

  getWorldMatrix(matrix) {

    mat4.copy(matrix, this.modelMatrix);

    let parent = this._parent;

    while (parent) {

      mat4.multiply(matrix, parent.modelMatrix, matrix);

      parent = parent._parent ? parent._parent : null;

    }

    return matrix;

  }

  getChunk(id) {

    for (let i = 0; i < this.chunks.length; ++i) {

      if (this.chunks[i].id == id) {
        return this.chunks[i];
      }

    }

    return null;

  }

  setShader(shader) {
    this.shader = shader;
  }

  setTexture(texture, slot) {
    const sceneOwner = this.getRootNode();

    const oldTexture = this.textures[slot];
    if (oldTexture && sceneOwner) {
      memoryProfiler.markMemoryReleased(oldTexture.pixelBuffer, sceneOwner);
    }

    this.textures[slot] = texture;

    if (sceneOwner) {
      memoryProfiler.markMemoryAllocated(texture.pixelBuffer, memoryType.GPU_TEXTURES, texture.width * texture.height * 4, sceneOwner);
    }

  }

  cleanupTextures() {
    const sceneOwner = this.getRootNode();

    for (const texture of this.textures) {
      if (sceneOwner) {
        memoryProfiler.markMemoryReleased(texture.pixelBuffer, sceneOwner);
      }
      texture.cleanup();
    }

    this.textures.length = 0;

  }

  castRay(origin, direction, dest) {

    const chunks = this.chunks;

    const originLocal = _VEC4_TMP;
    const directionLocal = _VEC4_TMP2;

    vec4.set(directionLocal, direction[0], direction[1], direction[2], 0);
    vec4.set(originLocal, origin[0], origin[1], origin[2], 1);

    const toLocalMat4 = mat4.invert(_MAT4_TMP, this.modelMatrix);

    if (!toLocalMat4) {
      output.error(output.DEVELOPERS_ERROR, "Couldn't invert model matrix " + mat4.str(this.modelMatrix) + ". " +
        "Position: " + vec3.str(this.trans) + ". " +
        "Rotation: " + quat.str(this.quat) + ". " +
        "Scale: " + vec3.str(this.scale) + ". ");
      return false;
    }

    vec4.transformMat4(originLocal, originLocal, toLocalMat4);
    vec4.transformMat4(directionLocal, directionLocal, toLocalMat4);

    let sqrDist = Infinity;

    let hitted = false;

    for (let i = 0; i < chunks.length; ++i) {

      const geometry = chunks[i].geometry;
      const hitPoint = _VEC3_TMP;

      if (geometry.castRay(originLocal, directionLocal, hitPoint) < 0) {
        continue;
      }

      hitted = true;

      const dist = vec3.squaredDistance(hitPoint, originLocal);

      if (dist < sqrDist) {
        sqrDist = dist;
        vec3.copy(dest, hitPoint);
      }

    }

    if (hitted) {
      vec3.transformMat4(dest, dest, this.modelMatrix);
    }

    return hitted;

  }

  reportMemoryUsage() {
    const sceneOwner = this.getRootNode();

    for (const chunk of this.chunks) {
      chunk.requestMemoryUsageReport(sceneOwner);
    }

    for (const texture of this.textures) {
      memoryProfiler.markMemoryAllocated(texture.pixelBuffer, memoryType.GPU_TEXTURES, texture.width * texture.height * 4, sceneOwner);
    }

  }

  reportMemoryRelease() {
    const sceneOwner = this.getRootNode();

    for (const chunk of this.chunks) {
      chunk.reportMemoryReleaseFromOwner(sceneOwner);
    }

    for (const texture of this.textures) {
      memoryProfiler.markMemoryReleased(texture.pixelBuffer, sceneOwner);
    }

    this.reportImportedAttributesRelease();
  }

  hide() {
    this.isHidden = true;
  }

  show() {
    this.isHidden = false;
  }

  setVisibility(visible) {
    this.isHidden = !visible;
  }

  checkIsHidden() {
    return this.isHidden;
  }

  isEmpty() {
    return this.chunks.length == 0;
  }

  setDrawMode(mode) {
    for (let i = 0; i < this.chunks.length; ++i) {
      this.chunks[i].setDrawMode(mode);
    }
  }

  setDiffuseColor(r, g, b) {
    this.properties.diffuseColor[0] = r;
    this.properties.diffuseColor[1] = g;
    this.properties.diffuseColor[2] = b;
  }

  setOpacity(opacity) {
    this.properties.diffuseColor[3] = opacity;
  }

  getOpacity() {
    return this.properties.diffuseColor[3];
  }

  setReflectivity(reflectivity) {
    vec3.copy(this.properties.reflectivity, reflectivity);
  }

  setupTextures() {

    if (this.textures.length) {
      GL.setupTextures(this.textures);
    }

  }

  updateMatrix() {

    const needsMatUpdate = this.checkNeedsMatUpdate();

    super.updateMatrix();

    if (needsMatUpdate) {
      this.updateChunksWorldBoundings();
    }

  }

  updateChunksWorldBoundings() {

    for (let i = 0; i < this.chunks.length; ++i) {

      if (!this.chunks[i].isEmpty()) {
        this.updateChunkBBox(this.chunks[i]);
      }

    }

  }

  setFrustrumCulling(enabled) {

    for (let i = 0; i < this.chunks.length; ++i) {
      this.chunks[i].enableFrustumTest = enabled;
    }

  }

  updateBounding() {

    this.updateMatrix();

    minmaxUtils.reset(this.minmaxWorld);
    minmaxUtils.reset(this.minmaxLocal);

    for (const chunk of this.chunks) {

      const geometry = chunk.geometry;
      const positions = geometry.getItemBuffer("a_position");

      if (!positions) {
        continue;
      }

      for (let i = 0; i < positions.length; i += 3) {
        const vertex = vec3.set(_VEC3_TMP, positions[i], positions[i + 1], positions[i + 2]);

        minmaxUtils.expandPoint(this.minmaxLocal, vertex);
        vec3.transformMat4(vertex, vertex, this.modelMatrix);
        minmaxUtils.expandPoint(this.minmaxWorld, vertex);
      }

    }

    for (const child of this._children) {
      if (!child.isDrawable) continue;

      child.updateBounding();
      minmaxUtils.expandMinmax(this.minmaxWorld, child.minmaxWorld);
      minmaxUtils.expandMinmax(this.minmaxLocal, child.minmaxLocal);
    }

  }

  _updateVAO(chunk) {

    const VAOExtension = extensions.get("OES_vertex_array_object");

    if (VAOExtension) {

      const gl = GL.getContext();

      let vao = VAOExtension.createVertexArray();
      VAOExtension.bindVertexArray(vao);

      const chunkProperties = this._chunkProperties.get(chunk);
      const attributesMap = chunkProperties.attributesMap;

      chunk.attachBuffers(gl, attributesMap);

      VAOExtension.bindVertexArray(null);

      chunkProperties.vao = vao;

    }

  }

  _updateInstancedVAO(chunk, instanceObject, instanceDivisor) {

    const VAOExtension = extensions.get("OES_vertex_array_object");
    const instanceExtension = extensions.get("ANGLE_instanced_arrays");

    if (VAOExtension && instanceExtension) {

      const gl = GL.getContext();

      let vao = VAOExtension.createVertexArray();
      VAOExtension.bindVertexArray(vao);

      const chunkProperties = this._chunkProperties.get(chunk);
      const attributesMap = chunkProperties.attributesMap;
      const instancedAttributesMap = instanceObject.attributesMap;

      chunk.attachBuffers(gl, attributesMap);
      chunk.attachInstancedBuffers(gl, instanceExtension, instancedAttributesMap, instanceDivisor);

      VAOExtension.bindVertexArray(null);

      instanceObject.vao = vao;

    }

  }


  getVao(chunk) {
    return this._chunkProperties.get(chunk).vao;
  }

  updateAttributes(shader, chunk) {
    const sceneOwner = this.getRootNode();

    const chunkProperties = this._chunkProperties.get(chunk);

    if (chunkProperties.activeShaderProgram == shader.program) {
      return;
    }

    const map = [];
    const unlinkedAttributes = [];

    for (let attName in shader.attributesLocations) {

      const attribute = chunk.attributeBuffers[attName];

      if (!attribute) {
        if (shader.instancedMode) {
          unlinkedAttributes.push(attName);
        } else {
          output.error(output.DEVELOPERS_ERROR, "Geometry is incompatible with shader. Missing attribute: \"" + attName + "\"");
        }
        continue;
      }

      map.push(shader.attributesLocations[attName]);
      map.push(attribute);

    }
    chunkProperties.attributesMap = map;
    this._updateVAO(chunk);

    chunkProperties.instancedAttributes.clear();
    for (const importedModel of this.importedModelsForInstancing) {
      const attributesMapsForModel = [];
      chunkProperties.instancedAttributes.set(importedModel, attributesMapsForModel);
      for (const importedChunk of importedModel.chunks) {

        const instancedAttributesMap = [];
        for (const attName of unlinkedAttributes) {
          const remappedAttName = shader.instancedAttributesRemapping[attName];
          const attribute = importedChunk.attributeBuffers[remappedAttName];

          if (!attribute) {
            output.error(output.DEVELOPERS_ERROR, "Geometry is incompatible with shader. Missing attribute: \"" + attName + "\"");
            continue;
          }

          instancedAttributesMap.push(shader.attributesLocations[attName]);
          instancedAttributesMap.push(attribute);

          memoryProfiler.markMemoryAllocated(attribute.vbo, memoryType.GPU_GEOMETRY, attribute.byteLength, sceneOwner);
        }

        const attributesMapObject = {
          vao: null,
          instancesCount: importedChunk.geometry.getVerticesCount(),
          attributesMap: instancedAttributesMap,
        };
        attributesMapsForModel.push(attributesMapObject);
        this._updateInstancedVAO(chunk, attributesMapObject, shader.instanceDivisor);

      }
    }

    chunkProperties.activeShaderProgram = shader.program;
  }

  cleanup() {

    this.cleanupChunks();
    this.cleanupTextures();

    this.reportImportedAttributesRelease();
    this.importedModelsForInstancing.length = 0;

  }

  cleanupChunks() {
    for (const chunk of this.chunks) {
      chunk.cleanup();
    }

    this.chunks.length = 0;
  }

  getAttributesMap(chunk) {
    return this._chunkProperties.get(chunk).attributesMap;
  }

  getInstancedAttributes(chunk) {
    return this._chunkProperties.get(chunk).instancedAttributes;
  }

  chunkFrustumTest(chunk, planes) {
    const chunkProperties = this._chunkProperties.get(chunk);
    chunkProperties.inFrustum = true;

    if (!chunk.enableFrustumTest) {
      return chunkProperties.inFrustum;
    }

    const bbox = chunkProperties.bbox;

    let minX, minY, minZ, maxX, maxY, maxZ;

    for (let i = 0; i < 6; ++i) {

      const plane = planes[i];

      // X axis
      if (plane[0] > 0) {
        minX = bbox[0];
        maxX = bbox[3];
      } else {
        minX = bbox[3];
        maxX = bbox[0];
      }
      // Y axis
      if (plane[1] > 0) {
        minY = bbox[1];
        maxY = bbox[4];
      } else {
        minY = bbox[4];
        maxY = bbox[1];
      }
      // Z axis
      if (plane[2] > 0) {
        minZ = bbox[2];
        maxZ = bbox[5];
      } else {
        minZ = bbox[5];
        maxZ = bbox[2];
      }

      if (plane[0] * minX + plane[1] * minY + plane[2] * minZ + plane[3] > 0) {

        chunkProperties.inFrustum = false;

        return chunkProperties.inFrustum;

      }

      if (plane[0] * maxX + plane[1] * maxY + plane[2] * maxZ + plane[3] >= 0) {
        return chunkProperties.inFrustum;
      }

    }

    return chunkProperties.inFrustum;

  }

  updateChunkBBox(chunk) {

    const chunkProperties = this._chunkProperties.get(chunk);

    const minmaxIn = chunk.geometry.bbox;
    const minmaxOut = chunkProperties.bbox;

    minmaxUtils.recomputedFromRotated(minmaxOut, minmaxIn, this.modelMatrix);

  }

  setScheduleDrawQuery(chunk, shouldRun) {

    if (!GL.checkWebGL2()) {
      return;
    }

    const chunkProperties = this._chunkProperties.get(chunk);

    if (shouldRun) {

      chunkProperties.queryState = drawQueryState.SHOULD_RUN;

      if (!chunkProperties.drawQuery) {
        const gl = GL.getContext();
        chunkProperties.drawQuery = gl.createQuery();
      }

    } else {

      chunkProperties.queryState = drawQueryState.FINISHED;

    }

    chunkProperties.queryResult = 1;

  }

  isChunkInFrustum(chunk) {
    return this._chunkProperties.get(chunk).inFrustum;
  }

  getChunkBBox(chunk) {
    return this._chunkProperties.get(chunk).bbox;
  }

  getChunkQueryResult(chunk) {
    return this._chunkProperties.get(chunk).queryResult;
  }

  getChunkQueryState(chunk) {
    return this._chunkProperties.get(chunk).queryState;
  }

  getChunkDrawQuery(chunk) {
    return this._chunkProperties.get(chunk).drawQuery;
  }

  setChunkQueryResult(chunk, newResult) {
    this._chunkProperties.get(chunk).queryResult = newResult;
  }

  setChunkQueryState(chunk, newState) {
    this._chunkProperties.get(chunk).queryState = newState;
  }

  reportImportedAttributesRelease() {
    const sceneOwner = this.getRootNode();
    if (!sceneOwner) {
      return;
    }

    for (const chunk of this.chunks) {

      const chunkProperties = this._chunkProperties.get(chunk);
      for (const attributesMapsForModel of chunkProperties.instancedAttributes.values()) {
        for (const attributesMap of attributesMapsForModel) {
          for (let i = 0; i < attributesMap.length; i += 2) {
            const attribute = attributesMap[i + 1];
            memoryProfiler.markMemoryReleased(attribute.vbo, sceneOwner);
          }
        }
      }
    }
  }

  setSelectionEnabled(enabled) {
    //Todo: WS-1206
    if (!this.shader) {
      super.setSelectionEnabled();
      return;
    }

    this.shader.setDirective("SELECTION", enabled);
  }

  setColorMode(colorMode) {
    //Todo: WS-1206
    if (!this.shader) {
      super.setColorMode();
      return;
    }

    if (this.shader instanceof DefaultShader) this.shader.setDirective("COLOR_TYPE", colorMode);
  }

  setTransparencyEnabled(enabled) {
    //Todo: WS-1206
    if (!this.shader) {
      super.setTransparencyEnabled();
      return;
    }

    this.shader.transparent = enabled;
  }

  fixFacesInversion() {
    if (!(this.shader instanceof DefaultShader)) {
      output.error(output.DEVELOPERS_ERROR, "object uses non-default shader. Triangles orientation could be incorrect");
      return;
    }

    const scaleSign = this.scale.reduce((accumulator, currentValue) => accumulator * currentValue);
    this.shader.setDirective("INVERT_FACES", scaleSign < 0);
    this.shader.setCullingFrontFace(scaleSign < 0 ? cullingFrontFace.CLOCK_WISE : cullingFrontFace.COUNTER_CLOCK_WISE);
  }

}
